import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import { FiMoon, FiSun } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../../theme/ThemeModeContext";

type Props = { size?: number; variant?: "icon" | "rail" };

/**
 * Light/dark switch wired to ThemeModeProvider.
 *
 * The tooltip and the accessible name were hardcoded English, which the
 * sidebar footer showed to every user regardless of the selected language —
 * the one untranslated string in an otherwise fully localised panel.
 */
export const ThemeToggle: React.FC<Props> = ({ size = 18, variant = "icon" }) => {
  const { isDark, toggleMode } = useThemeMode();
  const { t } = useTranslation();

  const label = isDark
    ? t("sidebar.themeToLight", "Switch to light mode")
    : t("sidebar.themeToDark", "Switch to dark mode");

  const railSx =
    variant === "rail"
      ? {
          color: "var(--c-nav-text)",
          "&:hover": { bgcolor: "rgba(96,165,250,0.16)" },
        }
      : { color: "text.secondary", "&:hover": { bgcolor: "action.hover" } };

  return (
    <Tooltip title={label} placement="right">
      <IconButton
        onClick={toggleMode}
        aria-label={label}
        sx={{
          borderRadius: 2,
          transition: "transform 200ms ease",
          "&:active": { transform: "scale(0.9)" },
          /* The default MUI focus ring is invisible on the dark rail. */
          "&:focus-visible": { outline: "2px solid #60A5FA", outlineOffset: 2 },
          ...railSx,
        }}
      >
        {isDark ? <FiSun size={size} /> : <FiMoon size={size} />}
      </IconButton>
    </Tooltip>
  );
};
